/**
 * Bootstrap Orchestrator
 * ======================
 * 
 * Manages post-authentication bootstrap with deterministic completion.
 * Ensures claims are propagated to the token before proceeding.
 * 
 * Work Order: PROD Login "First Attempt Fails, Second Attempt Works"
 * 
 * Key Features:
 * - Explicit async/await instead of fire-and-forget
 * - Force token refresh after onLoginSetClaims
 * - Bounded retry with polling for claims
 * - Clear success/timeout/error states
 */

import { User } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '@/firebase';
import { loginLogger } from './loginLogger';
import { hasProClaim } from './claimsUtils';

export interface BootstrapResult {
  success: boolean;
  hasUserDoc: boolean;
  hasClaims: boolean;
  claimsSet: {
    isAdmin: boolean;
    isPro: boolean;
    isDemo: boolean;
  };
  warnings: string[];
  timeoutOccurred: boolean;
}

const BOOTSTRAP_TIMEOUT = 8000; // 8 seconds total
const CLAIMS_POLL_INTERVAL = 500; // Poll every 500ms
const CLAIMS_POLL_MAX_ATTEMPTS = 10; // Max 5 seconds of polling

/**
 * Run post-login bootstrap with deterministic completion
 */
export async function runLoginBootstrap(user: User): Promise<BootstrapResult> {
  loginLogger.log('BOOTSTRAP_START', { uid: user.uid });

  const functions = getFunctions(app);
  const warnings: string[] = [];
  let hasUserDoc = false;
  let hasClaims = false;
  let timeoutOccurred = false;

  // Step 0: Check existing claims (skip onLoginSetClaims for demo users with valid claims)
  let existingClaims: any = {};
  try {
    const tokenResult = await user.getIdTokenResult(false); // Don't force refresh yet
    existingClaims = tokenResult.claims;
    
    const isDemo = existingClaims.demo === true;
    const hasExistingPro = hasProClaim(existingClaims);
    const hasAdmin = existingClaims.admin === true;
    
    if (isDemo && (hasExistingPro || hasAdmin)) {
      loginLogger.log('BOOTSTRAP_SKIP_CLAIMS', { 
        reason: 'demo-user-with-valid-claims',
        isDemo,
        hasExistingPro,
        hasAdmin
      });
      
      // Demo user with valid claims - skip onLoginSetClaims entirely
      hasClaims = true;
      hasUserDoc = true; // Assume user doc exists for demo users
      
      return {
        success: true,
        hasUserDoc: true,
        hasClaims: true,
        claimsSet: {
          isAdmin: hasAdmin,
          isPro: hasExistingPro,
          isDemo: true,
        },
        warnings: [],
        timeoutOccurred: false,
      };
    }
  } catch (error) {
    console.warn('[bootstrap] Failed to check existing claims, continuing with full bootstrap:', error);
  }

  // Step 1: Ensure user document exists
  try {
    loginLogger.log('BOOTSTRAP_ENSURE_USER_DOC_START');
    const startTime = Date.now();

    await Promise.race([
      httpsCallable(functions, 'ensureUserDoc')({}),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), 3000)
      ),
    ]);

    hasUserDoc = true;
    loginLogger.log('BOOTSTRAP_ENSURE_USER_DOC_OK', {
      duration: Date.now() - startTime,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'unknown';
    loginLogger.log('BOOTSTRAP_ENSURE_USER_DOC_ERR', { error: message });

    if (message === 'timeout') {
      timeoutOccurred = true;
      warnings.push('User document creation timed out (non-critical)');
    } else {
      warnings.push(`User document error: ${message}`);
    }
  }

  // Step 2: Set custom claims
  let claimsResponse: { isAdmin?: boolean; hasPro?: boolean } | null = null;
  try {
    loginLogger.log('BOOTSTRAP_SET_CLAIMS_START');
    const startTime = Date.now();

    const result = await Promise.race([
      httpsCallable(functions, 'onLoginSetClaims')({}),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), 3000)
      ),
    ]);

    claimsResponse = (result as { data?: { isAdmin?: boolean; hasPro?: boolean } })?.data || null;

    loginLogger.log('BOOTSTRAP_SET_CLAIMS_OK', {
      duration: Date.now() - startTime,
      claimsResponse,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'unknown';
    loginLogger.log('BOOTSTRAP_SET_CLAIMS_ERR', { error: message });

    if (message === 'timeout') {
      timeoutOccurred = true;
      warnings.push('Claims setting timed out (non-critical)');
    } else {
      warnings.push(`Claims error: ${message}`);
    }
  }

  // Step 3: Force token refresh (critical for claims propagation)
  try {
    loginLogger.log('IDTOKEN_REFRESH_START');
    const startTime = Date.now();

    await user.getIdToken(true); // Force refresh

    loginLogger.log('IDTOKEN_REFRESH_OK', {
      duration: Date.now() - startTime,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'unknown';
    loginLogger.log('IDTOKEN_REFRESH_ERR', { error: message });
    warnings.push(`Token refresh error: ${message}`);
  }

  // Step 4: Poll for claims to appear on token
  const claimsSet = await pollForClaims(user);
  hasClaims = claimsSet.isAdmin || claimsSet.isPro || claimsSet.isDemo;

  // If claims function indicated admin/pro but they're not on token yet, log warning
  if (claimsResponse?.isAdmin && !claimsSet.isAdmin) {
    warnings.push('Admin claim set by function but not propagated to token yet');
  }
  if (claimsResponse?.hasPro && !claimsSet.isPro) {
    warnings.push('Pro claim set by function but not propagated to token yet');
  }

  const success = hasUserDoc && (hasClaims || warnings.length === 0);

  return {
    success,
    hasUserDoc,
    hasClaims,
    claimsSet,
    warnings,
    timeoutOccurred,
  };
}

/**
 * Poll for claims to appear on the token (with bounded retry)
 */
async function pollForClaims(user: User): Promise<{
  isAdmin: boolean;
  isPro: boolean;
  isDemo: boolean;
}> {
  loginLogger.log('CLAIMS_POLL_START');

  for (let attempt = 0; attempt < CLAIMS_POLL_MAX_ATTEMPTS; attempt++) {
    try {
      const tokenResult = await user.getIdTokenResult(false); // Don't force refresh every time
      const claims = tokenResult.claims;

      const isAdmin = claims.admin === true;
      const isPro = hasProClaim(claims);
      const isDemo = claims.demo === true;

      // If we found any claims, we're done
      if (isAdmin || isPro || isDemo) {
        loginLogger.log('CLAIMS_POLL_OK', {
          attempt: attempt + 1,
          isAdmin,
          isPro,
          isDemo,
        });

        return { isAdmin, isPro, isDemo };
      }

      // Wait before next attempt
      if (attempt < CLAIMS_POLL_MAX_ATTEMPTS - 1) {
        await new Promise((resolve) => setTimeout(resolve, CLAIMS_POLL_INTERVAL));
      }
    } catch (error) {
      console.warn('[pollForClaims] Error checking claims:', error);
    }
  }

  // Timeout - no claims found
  loginLogger.log('CLAIMS_POLL_TIMEOUT', {
    attempts: CLAIMS_POLL_MAX_ATTEMPTS,
    totalTime: CLAIMS_POLL_MAX_ATTEMPTS * CLAIMS_POLL_INTERVAL,
  });

  return { isAdmin: false, isPro: false, isDemo: false };
}
